import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { RootState } from '@stores/store';
import { genericTexts } from '@utils/translations/en';
import AddIcon from '@mui/icons-material/Add';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DrawerComponent from '../../../../components/Drawer/Drawer';
import FilterSection from './FilterSection';
import { SubmoduleFilterMap } from '@stores/Modules/types/modules';
import { SubmoduleScopeType, UserSubmoduleMap } from '@api/company';
import { USER_ROLES } from '../UserDetails';
import { inputFieldStyles, menuItemStyles, selectStyles } from '@utils/styles';
import { EXTERNAL_SUBMODULE_TYPES } from '@constants/constants';

export interface ModulesSubModulesProps {
  submoduleList: UserSubmoduleMap[];
  setSubmoduleList: (value: UserSubmoduleMap[]) => void;
  userRole: USER_ROLES;
  disabled?: boolean;
}

export interface FilterOptions {
  filterOptions: string[];
  selectedFilters: Set<string>;
  filterMap: SubmoduleFilterMap | null;
}

export interface SubModuleData {
  moduleName: string;
  subModuleOptions: Array<{ label: string; value: string }>;
  filters: Record<string, FilterOptions>;
  accessType?: string;
}

export interface ModuleData {
  moduleOptions: Array<{ label: string; value: string }>;
  modules: Record<string, SubModuleData>;
}

const isSubmoduleTakenByOtherRow = (
  submoduleList: UserSubmoduleMap[],
  moduleId: string,
  submoduleId: string,
  currentRowIndex: number,
): boolean =>
  submoduleList.some(
    (item, i) =>
      i !== currentRowIndex &&
      item.moduleId === moduleId &&
      item.submoduleId === submoduleId &&
      !item.membershipId,
  );

const isModuleOptionAvailable = (
  moduleOptionId: string,
  currentModuleId: string | undefined,
  submoduleList: UserSubmoduleMap[],
  subModuleOptions: Array<{ label: string; value: string }>,
  currentRowIndex: number,
): boolean => {
  if (moduleOptionId === currentModuleId) return true;

  const hasSubmoduleAlreadyAdded = subModuleOptions.some(
    (subModuleOption) =>
      !isSubmoduleTakenByOtherRow(
        submoduleList,
        moduleOptionId,
        subModuleOption.value,
        currentRowIndex,
      ),
  );
  return hasSubmoduleAlreadyAdded;
};

const isSubModuleOptionAvailable = (
  submoduleList: UserSubmoduleMap[],
  moduleId: string,
  submoduleOptionId: string,
  currentSubmoduleId: string,
  currentRowIndex: number,
): boolean => {
  if (submoduleOptionId === currentSubmoduleId) return true;

  if (
    submoduleList.some(
      (item) =>
        item.moduleId === moduleId && item.submoduleId === submoduleOptionId && item.membershipId,
    )
  ) {
    return true;
  }

  return !isSubmoduleTakenByOtherRow(submoduleList, moduleId, submoduleOptionId, currentRowIndex);
};

const hasFullAccess = (
  accessType?: string | null,
  scopeType?: SubmoduleScopeType | null,
): boolean => {
  if (!accessType || !scopeType) return false;
  return accessType === 'ALL' && scopeType === SubmoduleScopeType.GLOBAL;
};

const hasCompanyAccess = (
  accessType?: string | null,
  scopeType?: SubmoduleScopeType | null,
): boolean => {
  if (!accessType || !scopeType) return false;
  return accessType === 'ALL' && scopeType === SubmoduleScopeType.COMPANY;
};

const getSubmoduleAccessLabel = ({
  accessType,
  scopeType,
  filterIds,
}: Pick<UserSubmoduleMap, 'accessType' | 'scopeType' | 'filterIds'>): string => {
  if (hasFullAccess(accessType, scopeType)) {
    return 'Full Access';
  }
  if (hasCompanyAccess(accessType, scopeType)) {
    return 'Company Access';
  }
  return filterIds.length ? `${filterIds.length} Selected` : 'Select Filters';
};

const ModulesSubModules: FC<ModulesSubModulesProps> = ({
  submoduleList,
  setSubmoduleList,
  userRole,
  disabled = false,
}) => {
  // Theme
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux
  const modulesState = useSelector((state: RootState) => state?.ModulesData);

  // State
  const [moduleData, setModuleData] = useState<ModuleData>({
    modules: {},
    moduleOptions: [],
  });
  const [openDrawer, toggleOpenDrawer] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedModuleName, setSelectedModuleName] = useState<string>('');
  const [selectedSubModule, setSelectedSubModule] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Use Effects
  useEffect(() => {
    if (modulesState.loadCount === 0 || !modulesState.filtersFetched) return;

    const modulesMap: Record<string, SubModuleData> = {};

    const moduleOptions: { label: string; value: string }[] =
      modulesState.userManagementModules.map((module) => {
        const subModuleOptions = (module.submodules || [])
          .filter((subModule) => !EXTERNAL_SUBMODULE_TYPES.includes(subModule.submoduleType || ''))
          .map((subModule) => ({
            label: subModule.name,
            value: subModule.id,
          }));

        const filters: Record<string, FilterOptions> = (module.submodules || []).reduce(
          (acc, subModule) => {
            let subModuleFilters: SubmoduleFilterMap = {};
            if (subModule.filters?.length) {
              for (const filter of subModule.filters) {
                const { submoduleFilterName } = filter;
                if (!subModuleFilters[submoduleFilterName]) {
                  subModuleFilters[submoduleFilterName] = [];
                }

                subModuleFilters[submoduleFilterName].push(filter);
              }
            } else {
              subModuleFilters = {};
            }

            acc[subModule.id] = {
              filterOptions: (subModule.filters || []).map((filter) => filter.filterValue),
              selectedFilters: new Set(),
              filterMap: subModuleFilters,
            };
            return acc;
          },
          {} as Record<string, FilterOptions>,
        );

        modulesMap[module.id] = {
          moduleName: module.name,
          subModuleOptions,
          filters,
        };
        return { label: module.name, value: module.id };
      });

    for (const submodule of submoduleList) {
      const { moduleId, submoduleId, filterIds } = submodule;

      if (moduleId && submoduleId && filterIds?.length) {
        modulesMap[moduleId].filters[submoduleId].selectedFilters = new Set(filterIds);
      }
    }

    setModuleData({
      moduleOptions,
      modules: modulesMap,
    });
  }, [modulesState, submoduleList]);

  const handleModuleSelection = (moduleId: string, index: number) => {
    const _submoduleList = [...submoduleList];

    _submoduleList[index] = {
      ..._submoduleList[index],
      moduleId,
      submoduleId: '',
      filterIds: [],
      accessType: undefined,
      scopeType: undefined,
    };

    setSubmoduleList(_submoduleList);
  };

  const handleSubModuleSelection = (subModuleId: string, index: number) => {
    const _submoduleList = [...submoduleList];

    _submoduleList[index] = {
      ..._submoduleList[index],
      submoduleId: subModuleId,
      filterIds: [],
      accessType: undefined,
      scopeType: undefined,
    };

    setSubmoduleList(_submoduleList);
  };

  const handleDeleteRow = (index: number) => {
    const _submoduleList = [...submoduleList];
    _submoduleList.splice(index, 1);
    setSubmoduleList(_submoduleList);
  };

  const handleFiltersClick = (index: number) => {
    setSelectedModule(submoduleList[index].moduleId as string);
    setSelectedModuleName(moduleData.modules[submoduleList[index].moduleId as string]?.moduleName);
    setSelectedSubModule(submoduleList[index].submoduleId);
    setSelectedFilters(submoduleList[index].filterIds);
    setSelectedIndex(index);
    toggleOpenDrawer(true);
  };

  const handleFilterSave = (
    data: {
      type: string;
      options: { id: string; name: string; position: number }[];
    }[],
    allAccessType: boolean = false,
  ) => {
    const _submoduleList = [...submoduleList];

    /**
     * @accessType: ALL -> @scopeType: GLOBAL
     * @accessType: RLS -> @scopeType: RESTRICTED
     */

    if (allAccessType) {
      // Full access is checked
      _submoduleList[selectedIndex].accessType = 'ALL';
      _submoduleList[selectedIndex].scopeType = SubmoduleScopeType.GLOBAL;
      _submoduleList[selectedIndex].filterIds = [];
    } else if (selectedIndex !== -1 && data.length) {
      // Full access is unchecked and filters exist
      _submoduleList[selectedIndex].accessType = 'RLS';
      _submoduleList[selectedIndex].scopeType = SubmoduleScopeType.RESTRICTED;
      _submoduleList[selectedIndex].filterIds = data[0].options.map(
        (filter: { id: string; name: string; position: number }) => filter.id,
      );
    } else {
      // Full access is unchecked and no filters exist
      _submoduleList[selectedIndex].accessType = 'RLS';
      _submoduleList[selectedIndex].scopeType = SubmoduleScopeType.RESTRICTED;
      _submoduleList[selectedIndex].filterIds = [];
    }

    setSubmoduleList(_submoduleList);
  };

  const addNewModuleSelection = () => {
    setSubmoduleList([
      ...submoduleList,
      { moduleId: '', submoduleId: '', membershipId: null, filterIds: [] },
    ]);
  };

  if (userRole === 'ADMIN') {
    return null;
  }

  const selectedSubmodule = selectedIndex >= 0 ? submoduleList[selectedIndex] : undefined;

  return (
    <Stack
      gap={'1rem'}
      mt={'1rem'}
      sx={{
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Typography fontSize={'1rem'} fontWeight={500} color={'#6B7280'}>
        Module Details
      </Typography>

      {submoduleList.map(({ moduleId, submoduleId, hidden }, index) => (
        <Box
          key={`${index}--${moduleId}--${submoduleId}`}
          sx={{ display: hidden ? 'none' : 'block' }}
        >
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr 1fr 40px" // 1fr for each Select, 40px for IconButton
            gap="1rem"
            alignItems="center"
          >
            <FormControl
              sx={{
                ...(isMobile
                  ? {
                      gridColumn: 'span 4',
                    }
                  : {}),
              }}
              fullWidth
            >
              <InputLabel
                sx={{
                  fontSize: '14px',
                }}
                id={'module-label-' + index}
              >
                Module
              </InputLabel>
              <Select
                labelId={'module-label-' + index}
                label="Module"
                value={moduleId}
                onChange={(e) => handleModuleSelection(e.target.value, index)}
                fullWidth
                sx={{
                  ...selectStyles,
                  height: '56px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {moduleData.moduleOptions
                  .filter((option) =>
                    isModuleOptionAvailable(
                      option.value,
                      moduleId,
                      submoduleList,
                      moduleData.modules[option.value]?.subModuleOptions ?? [],
                      index,
                    ),
                  )
                  .map((option) => (
                    <MenuItem
                      key={`${index}--${option.value}`}
                      value={option.value}
                      sx={{
                        ...menuItemStyles,
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              sx={{
                ...(isMobile
                  ? {
                      gridColumn: 'span 4',
                    }
                  : {}),
              }}
            >
              <InputLabel
                sx={{
                  fontSize: '14px',
                }}
                id={'submodule-label-' + index}
              >
                Sub Module
              </InputLabel>

              <Select
                value={submoduleId}
                onChange={(e) => handleSubModuleSelection(e.target.value, index)}
                fullWidth
                labelId={'submodule-label-' + index}
                label="Sub Module"
                disabled={
                  !moduleId || moduleData.modules[moduleId as string]?.subModuleOptions.length === 0
                }
                sx={{
                  ...selectStyles,
                  height: '56px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {moduleData.modules[moduleId as string]?.subModuleOptions
                  .filter((option) =>
                    isSubModuleOptionAvailable(
                      submoduleList,
                      moduleId as string,
                      option.value,
                      submoduleId,
                      index,
                    ),
                  )
                  .map((option) => (
                    <MenuItem
                      key={`${index}--${option.value}`}
                      value={option.value}
                      sx={{
                        ...menuItemStyles,
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box
              display="flex"
              justifyContent="space-between"
              // onClick={() =>
              //   moduleData.modules[submoduleList[index].moduleId as string]?.filters[
              //     submoduleList[index].submoduleId
              //   ]?.filterOptions.length && handleFiltersClick(index)
              // }
              onClick={() => handleFiltersClick(index)}
              sx={{
                ...selectStyles,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                height: '56px',
                alignItems: 'center',
                color: '#00000080',
                ...(isMobile
                  ? {
                      gridColumn: 'span 3',
                    }
                  : {}),
                // opacity: moduleData.modules[submoduleList[index].moduleId as string]?.filters[
                //   submoduleList[index].submoduleId
                // ]?.filterOptions.length
                //   ? '1'
                //   : '0.5',
                // cursor: moduleData.modules[submoduleList[index].moduleId as string]?.filters[
                //   submoduleList[index].submoduleId
                // ]?.filterOptions.length
                //   ? 'pointer'
                //   : 'not-allowed',
                opacity: 1,
                cursor: 'pointer',
              }}
            >
              <Typography sx={{ ...inputFieldStyles }}>
                {getSubmoduleAccessLabel(submoduleList[index])}
              </Typography>
              <ArrowRightIcon sx={{ color: '#49454F' }} />
            </Box>
            <IconButton
              onClick={() => handleDeleteRow(index)}
              sx={{
                width: '40px',
                ':hover': {
                  background: 'none',
                },
              }}
            >
              <DeleteOutlineIcon
                sx={{ height: '1.5rem', width: '1.5rem' }}
                htmlColor={theme.palette.error.main}
              />
            </IconButton>
          </Box>

          {isMobile && submoduleList.length !== index + 1 && <Divider sx={{ mt: '1rem' }} />}
        </Box>
      ))}

      <Box>
        <Button
          onClick={addNewModuleSelection}
          variant="contained"
          disabled={submoduleList.filter((item) => !!!item.submoduleId).length > 0}
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#F6F8F9',
            borderRadius: '100px',
            textTransform: 'capitalize',
            px: 4,
            py: 1,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
          startIcon={<AddIcon />}
        >
          {genericTexts.addNewModule}
        </Button>
      </Box>

      {openDrawer && (
        <DrawerComponent
          open={openDrawer}
          anchor="right"
          close={(e) => {
            toggleOpenDrawer(false);
          }}
          height="98vh"
          zIndex={9999}
        >
          <FilterSection
            moduleName={selectedModuleName || ''}
            moduleId={selectedModule || ''}
            subModuleId={selectedSubModule || ''}
            filterMap={moduleData.modules[selectedModule]?.filters[selectedSubModule]?.filterMap}
            close={() => toggleOpenDrawer(false)}
            handleFilterSave={(data, allAccessType) => handleFilterSave(data, allAccessType)}
            selectedFilters={selectedFilters}
            allAccessType={hasFullAccess(
              selectedSubmodule?.accessType,
              selectedSubmodule?.scopeType,
            )}
            isViewOnly={
              !!selectedSubmodule?.membershipId &&
              hasCompanyAccess(selectedSubmodule?.accessType, selectedSubmodule?.scopeType)
            }
          />
        </DrawerComponent>
      )}
    </Stack>
  );
};

export default ModulesSubModules;
